import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useDispatch } from 'react-redux';
import { PagingContext } from '../../../../contexts/paging-context';
import { entitiesLoad } from '../../../../store/entities/entities.actions';
import { createLookupHash } from '../../../../services/hash-service';
import { getPresetGroups } from '../../../../store/products/products.api';
import PropTypes from 'prop-types';
import { emptyArray } from 'ui-components/default-props/empty';
import get from 'lodash.get';
import { descending } from '../../../../constants/sortDirections';

const PresetGroupsContext = createContext({});

const PresetGroupsContextProvider = ({ children }) => {
  const orderBy = 'name';
  const dispatch = useDispatch();
  const [presetGroupIds, setPresetGroupIds] = useAsyncSafeState([]);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const { page, pageSize } = useContext(PagingContext);
  const [direction, setDirection] = useAsyncSafeState(descending);
  const [totalRecords, setTotalRecords] = useAsyncSafeState(0);
  const [needsReload, setNeedsReload] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState('');

  useEffect(() => {
    const presetGroupPromise = getPresetGroups(page, pageSize, orderBy, direction);
    presetGroupPromise.promise
      .then((response) => {
        const presetGroups = get(response, 'data.profiles', emptyArray);
        const ids = presetGroups.map((pg) => pg.productProfileId);
        setPresetGroupIds(ids);
        const totalRecordCount = get(response, 'data.totalCount', 0);
        setTotalRecords(totalRecordCount);
        setIsLoading(false);
        const entitiesToLoad = {
          entities: {
            presetGroups: createLookupHash(presetGroups, 'productProfileId'),
          },
        };
        dispatch(entitiesLoad(entitiesToLoad));
      })
      .catch(() => {
        setIsLoading(false);
        dispatch(
          entitiesLoad({
            entities: {
              presetGroups: {},
            },
          }),
        );
      });
    return () => presetGroupPromise?.cancel();
  }, [page, pageSize, orderBy, direction, dispatch, needsReload, setPresetGroupIds, setIsLoading, setDirection, setTotalRecords]);

  const value = useMemo(
    () => ({
      presetGroupIds,
      isLoading,
      totalRecords,
      direction,
      setDirection,
      needsReload,
      setNeedsReload,
      isModalOpen,
      setIsModalOpen,
      selectedPresetId,
      setSelectedPresetId,
    }),
    [
      presetGroupIds,
      isLoading,
      totalRecords,
      direction,
      setDirection,
      needsReload,
      setNeedsReload,
      isModalOpen,
      setIsModalOpen,
      selectedPresetId,
      setSelectedPresetId,
    ],
  );

  return <PresetGroupsContext.Provider value={value}>{children}</PresetGroupsContext.Provider>;
};

PresetGroupsContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export { PresetGroupsContext };

export default PresetGroupsContextProvider;
